import React, { useState } from "react";

type Props = {
  text?: string, className? : string, disabled?: boolean, size?: "sm" | "md" | "lg" | "xs",
  LeadingIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>,
  TrailingIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>,
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => any,
  color?: "purple" | "green" | "white" | "opaque",
  border?: boolean, type?: "button" | "submit",
  loadingPreview?: string,
};

const HeButton = ({
  onClick = () => {},
  text = "",
  size = "md",
  disabled = false,
  className,
  LeadingIcon,
  TrailingIcon,
  loadingPreview = "Processing...",
  color = "white",
  border = true,
  type = "button",
} : Props) => {
  const [loading, setLoading] = useState<boolean>(false);

  let buttonStyle = "whitespace-nowrap border flex items-center rounded-md font-medium shadow-sm ";
  switch (color) {
    case "green":
      buttonStyle += " bg-emerald-600 text-white hover:bg-emerald-700 focus:bg-emerald-600 focus:outline-none focus:shadow-textBox focus:shadow-emerald-100";
      break;
    case "white":
      buttonStyle += " border-gray-300 bg-white text-gray-500 hover:bg-secondary-100 hover:text-secondary-700 focus:bg-white focus:text-primary-600 focus:border-primary-600 focus:shadow-textBox";
      break;
    case "purple":
      buttonStyle += "  bg-primary-600 text-white hover:bg-primary-700 focus:bg-primary-600 focus:outline-none focus:shadow-textBox disabled:bg-gray-400";
      break;

    case "opaque":
      buttonStyle += "  border-purple-500 bg-none text-purple-500 hover:text-purple-600 hover:border-purple-600 hover:bg-gray-50 focus:bg-white focus:text-primary-600 focus:border-primary-600 focus:shadow-textBox";
      break;
    default:
  }

  if (!border) {
    buttonStyle += " border-none border-transparent";
  }

  let leadingIconStyle = "";
  let trailingIconStyle = "";
  if (size === "xs") {
    buttonStyle += " p-1 text-xs";
    leadingIconStyle = "h-4 w-4";
    trailingIconStyle = "h-4 w-4";
  } else if (size === "sm") {
    buttonStyle += " px-4 py-2 text-sm";
    leadingIconStyle = "-ml-1 mr-2 h-5 w-5";
    trailingIconStyle = "ml-2 -mr-1 h-5 w-5";
  } else if (size === "md") {
    buttonStyle += " px-4 py-2 text-base";
    leadingIconStyle = "-ml-1 mr-2 h-5 w-5";
    trailingIconStyle = "ml-2 -mr-1 h-5 w-5";
  } else if (size === "lg") {
    buttonStyle += " px-6 py-3 text-base";
    leadingIconStyle = "-ml-1 mr-2 h-5 w-5";
    trailingIconStyle = "ml-2 -mr-1 h-5 w-5";
  }

  if (className) {
    buttonStyle = `${buttonStyle} ${className}`;
  }

  const clickHandler = async (e: any) => {
    setLoading(true);
    await onClick(e);
    setLoading(false);
  };

  return (
    <button
      // eslint-disable-next-line react/button-has-type
      type={type}
      disabled={disabled}
      className={buttonStyle}
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      onClick={(e) => { clickHandler(e); }}
    >
      {LeadingIcon && (
      <LeadingIcon className={`${leadingIconStyle} ${text ? "" : "ml-0 mr-0"}`} aria-hidden="true" />
      )}
      {!loading ? text : (
        <>
          <svg aria-hidden="true" className="mr-2 w-5 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-gray-800" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
          </svg>
          {loadingPreview}
        </>
      )}
      {TrailingIcon && (
      <TrailingIcon className={`${trailingIconStyle}  ${text ? "" : "ml-0 mr-0"}`} aria-hidden="true" />
      )}
    </button>
  );
};

export default HeButton;
